import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../app/store';
import axiosRequest from '../utils/axios';
import { steps } from '../containers/PageRouter/config';
import generateUUIDs from '../utils/generateUUIDs';

export interface UserState {
    userData: {
        phone: string | null;
        monthOfBirth: string | null;
        yearOfBirth: string | null;
        nicotineUser: string | null;
        tcAgreement: string | null;
        stepHomepage: string;
    };
    validateUrlCodeLoading: boolean;
    validateUrlCodeSuccess: boolean;
    validateUrlCodeError: string | null;
    stepData: any;
    winnerData: any;
    prevStepData: any;
    sessionToken: string | null;
    resendPhoneValidationCodeLoading: boolean;
    resendPhoneValidationCodeResponse: any;
    resendPhoneValidationCodeError: any | null;
    saveStepLoading: boolean;
    saveStepSuccess: boolean;
    saveStepError: string | null;
    saveNameLoading: boolean;
    saveNameSuccess: boolean;
    saveNameError: string | null;
}

interface AxiosError {
    response?: {
        statusMessage?: string;
        data?: {
            message?: string;
            invalidField?: {
                fieldErrorDescription?: string;
            };
        };
    };
    message: string;
}

const initialState: UserState = {
    userData: {
        phone: null,
        monthOfBirth: null,
        yearOfBirth: null,
        nicotineUser: null,
        tcAgreement: null,
        stepHomepage: steps.STEP_START
    },
    validateUrlCodeLoading: false,
    validateUrlCodeSuccess: false,
    validateUrlCodeError: null,
    stepData: null,
    winnerData: null,
    prevStepData: null,
    sessionToken: null,
    resendPhoneValidationCodeLoading: false,
    resendPhoneValidationCodeResponse: null,
    resendPhoneValidationCodeError: null,
    saveStepLoading: false,
    saveStepSuccess: false,
    saveStepError: null,
    saveNameLoading: false,
    saveNameSuccess: false,
    saveNameError: null
};

interface SaveStepError {
    status?: string;
    statusCode?: string;
    statusMessage?: string;
    invalidField?: string | null;
    response?: {
        wonPrizesHistory?: any[];
        wonChances?: {
            weekly: number | null;
            final: number | null;
        };
    };
}

export const validateUrlCode = createAsyncThunk(
    'user/validate-url-code',
    async (params: any, { rejectWithValue }) => {
        try {
            let ip: string | undefined;
            const ipifyRes = await fetch('https://api.ipify.org?format=json');
            if (ipifyRes.ok) {
                const json = (await ipifyRes.json()) as { ip: string };
                ip = json.ip;
            }

            const [storageUUID, cookieUUID, browserUUID] = await generateUUIDs();

            const payload = {
                ...params,
                ...(ip && { ip }),
                storageUUID,
                cookieUUID,
                browserUUID
            };

            const response = await axiosRequest.post('/validate-url-code', payload);
            if (response.data.status === 'failure') {
                throw new Error(
                    response?.data?.statusMessage ||
                        response?.data?.invalidField?.fieldErrorDescription
                );
            }
            return response.data.response;
        } catch (e) {
            const error = e as AxiosError;
            return rejectWithValue(
                error.response?.statusMessage || error.response?.data?.message || error.message
            );
        }
    }
);

export const resendPhoneValidationCode = createAsyncThunk(
    'user/resend-phone-validation-code',
    async (params: any, { rejectWithValue }) => {
        try {
            let ip: string | undefined;
            const ipifyRes = await fetch('https://api.ipify.org?format=json');
            if (ipifyRes.ok) {
                const json = (await ipifyRes.json()) as { ip: string };
                ip = json.ip;
            }
            const [storageUUID, cookieUUID, browserUUID] = await generateUUIDs();

            const payload = {
                ...params,
                ...(ip && { ip }),
                storageUUID,
                cookieUUID,
                browserUUID
            };

            const response = await axiosRequest.post('/resend-phone-validation-code', payload);
            return response.data.response;
        } catch (e) {
            const error = e as AxiosError;
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const saveStep = createAsyncThunk<any, any, { rejectValue: SaveStepError }>(
    'user/save-step',
    async (params: any, { rejectWithValue }) => {
        try {
            let ip: string | undefined;
            const ipifyRes = await fetch('https://api.ipify.org?format=json');
            if (ipifyRes.ok) {
                const json = (await ipifyRes.json()) as { ip: string };
                ip = json.ip;
            }
            const [storageUUID, cookieUUID, browserUUID] = await generateUUIDs();

            const payload = {
                ...params,
                ...(ip && { ip }),
                storageUUID,
                cookieUUID,
                browserUUID
            };

            const response = await axiosRequest.post('/save-step', payload);

            if (response.data.status === 'failure') {
                throw { response: { data: response.data } };
            }

            return response.data;
        } catch (e) {
            const error = e as AxiosError;
            return rejectWithValue(error.response?.data as SaveStepError);
        }
    }
);

export const saveName = createAsyncThunk(
    'user/save-name',
    async (params: any, { rejectWithValue }) => {
        try {
            let ip: string | undefined;
            const ipifyRes = await fetch('https://api.ipify.org?format=json');
            if (ipifyRes.ok) {
                const json = (await ipifyRes.json()) as { ip: string };
                ip = json.ip;
            }
            const [storageUUID, cookieUUID, browserUUID] = await generateUUIDs();

            const payload = {
                ...params,
                ...(ip && { ip }),
                storageUUID,
                cookieUUID,
                browserUUID
            };

            const response = await axiosRequest.put('/winners/update', payload);
            if (response.data.status === 'failure') {
                throw new Error(
                    response.data.invalidField?.fieldErrorDescription ||
                        response?.data?.statusMessage
                );
            }
            return response.data;
        } catch (e) {
            const error = e as AxiosError;
            return rejectWithValue(
                error.response?.statusMessage ||
                    error.response?.data?.invalidField?.fieldErrorDescription ||
                    error.message
            );
        }
    }
);

export const postUserTracking = createAsyncThunk(
    'user/tracking',
    async (params: any, { rejectWithValue }) => {
        try {
            let ip: string | undefined;
            const ipifyRes = await fetch('https://api.ipify.org?format=json');
            if (ipifyRes.ok) {
                const json = (await ipifyRes.json()) as { ip: string };
                ip = json.ip;
            }
            const [storageUUID, cookieUUID, browserUUID] = await generateUUIDs();

            const payload = {
                ...params,
                ...(ip && { ip }),
                storageUUID,
                cookieUUID,
                browserUUID
            };

            const response = await axiosRequest.post('tracking', payload);
            return response.data;
        } catch (e) {
            const error = e as AxiosError;
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const usersSlice = createSlice({
    name: 'users',
    initialState,
    reducers: {
        setUserData: (state, action) => {
            state.userData = action.payload;
        },
        setValidateUrlCodeSuccess: (state, action) => {
            state.validateUrlCodeSuccess = action.payload;
        },
        clearSaveStepError: (state) => {
            state.saveStepError = null;
        },
        clearValidateUrlCodeError: (state) => {
            state.validateUrlCodeError = null;
        },
        resetAllState: (state) => {
            Object.assign(state, initialState);
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(validateUrlCode.pending, (state) => {
                state.validateUrlCodeLoading = true;
                state.validateUrlCodeSuccess = false;
                state.validateUrlCodeError = null;
            })
            .addCase(validateUrlCode.fulfilled, (state, action) => {
                state.stepData = action.payload;
                state.sessionToken = action.payload.sessionToken;
                state.validateUrlCodeLoading = false;
                state.validateUrlCodeSuccess = true;
                state.validateUrlCodeError = null;
            })
            .addCase(validateUrlCode.rejected, (state, action) => {
                state.validateUrlCodeLoading = false;
                state.validateUrlCodeSuccess = false;
                state.validateUrlCodeError = action.payload as string;
            })
            .addCase(resendPhoneValidationCode.pending, (state) => {
                state.resendPhoneValidationCodeLoading = true;
                state.resendPhoneValidationCodeError = null;
            })
            .addCase(resendPhoneValidationCode.fulfilled, (state, action) => {
                state.resendPhoneValidationCodeLoading = false;
                state.resendPhoneValidationCodeResponse = action.payload;
                state.resendPhoneValidationCodeError = null;
                state.sessionToken = action.payload.sessionToken;
            })
            .addCase(resendPhoneValidationCode.rejected, (state, action) => {
                state.resendPhoneValidationCodeLoading = false;
                state.resendPhoneValidationCodeError = action.payload as string;
            })
            .addCase(saveStep.pending, (state) => {
                state.saveStepLoading = true;
                state.saveStepSuccess = false;
                state.saveStepError = null;
            })
            .addCase(saveStep.fulfilled, (state, action) => {
                state.saveStepLoading = false;
                state.saveStepSuccess = true;
                state.saveStepError = null;
                state.prevStepData = state.stepData;
                state.stepData = action.payload.response.response
                    ? action.payload.response.response
                    : action.payload.response;
                state.winnerData = action.payload.winner;
                state.sessionToken = action.payload.response.response
                    ? action.payload.response.response.sessionToken
                    : action.payload.response.sessionToken;
            })
            .addCase(saveStep.rejected, (state, action) => {
                state.saveStepLoading = false;
                state.saveStepSuccess = false;
                state.saveStepError = action.payload?.statusMessage ?? 'Oops. Ceva nu a mers bine.';
                state.stepData = action.payload?.response;
            })
            .addCase(saveName.pending, (state) => {
                state.saveNameLoading = true;
                state.saveNameSuccess = false;
                state.saveNameError = null;
            })
            .addCase(saveName.fulfilled, (state, action) => {
                state.saveNameLoading = false;
                state.saveNameSuccess = true;
                state.saveNameError = null;
            })
            .addCase(saveName.rejected, (state, action) => {
                state.saveNameLoading = false;
                state.saveNameSuccess = false;
                state.saveNameError = action.payload as string;
            });
    }
});

export const {
    setUserData,
    clearValidateUrlCodeError,
    setValidateUrlCodeSuccess,
    clearSaveStepError,
    resetAllState
} = usersSlice.actions;

export const selectUserData = (state: RootState) => state.users.userData;
export const selectValidateUrlCodeLoading = (state: RootState) =>
    state.users.validateUrlCodeLoading;
export const selectValidateUrlCodeSuccess = (state: RootState) =>
    state.users.validateUrlCodeSuccess;
export const selectValidateUrlCodeError = (state: RootState) => state.users.validateUrlCodeError;
export const selectStepData = (state: RootState) => state.users.stepData;
export const selectSessionToken = (state: RootState) => state.users.sessionToken;
export const selectResendPhoneValidationCodeLoading = (state: RootState) =>
    state.users.resendPhoneValidationCodeLoading;
export const selectResendPhoneValidationCodeResponse = (state: RootState) =>
    state.users.resendPhoneValidationCodeResponse;
export const selectSaveStepLoading = (state: RootState) => state.users.saveStepLoading;
export const selectSaveStepSuccess = (state: RootState) => state.users.saveStepSuccess;
export const selectSaveStepError = (state: RootState) => state.users.saveStepError;

export default usersSlice.reducer;
